import { useRef } from 'react';
import { Calendar, Loader2 } from 'lucide-react';

const QUICK_HOURS = [24, 48, 72] as const;

export function followUpInHours(hours: number): string {
  return new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
}

interface OpportunityFollowUpSchedulePanelProps {
  customDraft: string;
  saving?: boolean;
  onCustomDraftChange: (value: string) => void;
  onQuickSelect: (hours: number) => void;
  onCustomCommit: (value: string) => void;
  title?: string;
  className?: string;
}

export default function OpportunityFollowUpSchedulePanel({
  customDraft,
  saving = false,
  onCustomDraftChange,
  onQuickSelect,
  onCustomCommit,
  title = 'Follow up in',
  className = '',
}: OpportunityFollowUpSchedulePanelProps) {
  const customInputRef = useRef<HTMLInputElement>(null);

  const openPicker = () => {
    const input = customInputRef.current;
    if (!input) return;
    try {
      input.showPicker?.();
    } catch {
      // Some browsers require a direct user gesture.
    }
  };

  return (
    <div className={className}>
      <p className="text-sm font-semibold text-gray-900 mb-3">{title}</p>

      <div className="flex gap-2 mb-3">
        {QUICK_HOURS.map((hours) => (
          <button
            key={hours}
            type="button"
            disabled={saving}
            onClick={() => onQuickSelect(hours)}
            className="flex-1 rounded-lg border border-gray-200 bg-white px-2 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-50 active:bg-blue-50 active:border-blue-300 active:text-blue-700 disabled:opacity-50 touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            {hours}hrs
          </button>
        ))}
      </div>

      {/* Stacked layout: the native date/time picker (a bottom wheel / wide
          popover on iOS) opens over the field, so the commit button lives on its
          own full-width row below where the picker can't cover it. */}
      <div className="space-y-2">
        <div className="relative">
          <input
            ref={customInputRef}
            type="datetime-local"
            value={customDraft}
            disabled={saving}
            onFocus={openPicker}
            onClick={openPicker}
            onChange={(e) => onCustomDraftChange(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-3 pr-10 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 disabled:opacity-50 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
          />
          <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
        </div>
        <button
          type="button"
          disabled={saving || !customDraft.trim()}
          onClick={() => onCustomCommit(customDraft)}
          className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 active:bg-blue-800 disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          Set follow-up
        </button>
      </div>

      {saving ? (
        <div className="mt-2 flex items-center justify-center gap-1.5 text-xs text-gray-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Saving…
        </div>
      ) : null}
    </div>
  );
}
